import { useEffect, useRef, useState } from 'react';

interface ConfettiProps {
  active: boolean;
  onComplete?: () => void;
}

export function Confetti({ active, onComplete }: ConfettiProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!active || !containerRef.current) return;

    let destroyed = false;
    let canvas: HTMLCanvasElement | null = null;

    const run = async () => {
      // Dynamic import to avoid loading PixiJS unless needed
      const PIXI = await import('pixi.js');
      const gsap = (await import('gsap')).default;

      if (destroyed || !containerRef.current) return;

      const width = containerRef.current.clientWidth || 400;
      const height = containerRef.current.clientHeight || 300;

      const app = new PIXI.Application({
        width,
        height,
        backgroundAlpha: 0,
        antialias: true,
      });

      // PixiJS v7: app.view is the HTMLCanvasElement
      canvas = app.view as HTMLCanvasElement;
      if (!destroyed && containerRef.current) {
        containerRef.current.appendChild(canvas);
      }

      const colors = [0xFFD700, 0xFF6B6B, 0x4ECDC4, 0x45B7D1, 0x96CEB4, 0xFF8C42];

      for (let i = 0; i < 80; i++) {
        const size = 6 + Math.random() * 4;
        const particle = new PIXI.Graphics();
        particle.beginFill(colors[Math.floor(Math.random() * colors.length)]);
        particle.drawRect(0, 0, size, size);
        particle.endFill();
        particle.x = width / 2 + (Math.random() - 0.5) * 40;
        particle.y = height / 2;
        app.stage.addChild(particle);

        const angle = Math.random() * Math.PI * 2;
        const distance = 50 + Math.random() * 150;

        gsap.to(particle, {
          x: particle.x + Math.cos(angle) * distance,
          y: particle.y + Math.sin(angle) * distance + 100,
          alpha: 0,
          rotation: Math.random() * 720 - 360,
          duration: 1.2 + Math.random() * 0.8,
          ease: 'power2.out',
          onComplete: () => {
            particle.destroy();
            if (app.stage.children.length === 0 && !destroyed) {
              onComplete?.();
            }
          },
        });
      }
    };

    setVisible(true);
    run();

    return () => {
      destroyed = true;
      if (canvas && containerRef.current?.contains(canvas)) {
        containerRef.current.removeChild(canvas);
      }
    };
  }, [active, onComplete]);

  if (!active) return null;

  if (!visible) return null;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 100,
      }}
    />
  );
}
